import React from 'react';

import {
  Autocomplete,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { useGHAChains } from '~/hooks/apis/connection-google/useGHAChains';

import type { GHAScopeRuleCugTier } from '~/services/ConnectionGoogleService';

import { GHA_SCOPE_CUG_TIER_OPTIONS, GHA_SUPPLIER_LABELS, GHA_SUPPLIER_OPTIONS } from './constants';
import type { ScopeDimensionsFormState } from './utils';

interface Props {
  idPrefix: string;
  value: ScopeDimensionsFormState;
  onChange: <K extends keyof ScopeDimensionsFormState>(key: K, value: ScopeDimensionsFormState[K]) => void;
  /** What an empty countries list means differs by table: a scope rule can only pay landing
   *  countries, an exclusion vetoes every country. */
  countriesEmptyHelper: string;
}

/** The four scope dimensions both scoped tables share (suppliers / tiers / chains / viewer
 *  countries) — one implementation so the dialogs cannot drift apart. */
export default function ScopeDimensionFields({ idPrefix, value, onChange, countriesEmptyHelper }: Props) {
  const { data: chainOptions } = useGHAChains();

  return (
    <>
      <Stack direction="row" spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel id={`${idPrefix}-suppliers`} shrink>
            Suppliers
          </InputLabel>
          <Select
            labelId={`${idPrefix}-suppliers`}
            label="Suppliers"
            multiple
            displayEmpty
            notched
            value={value.suppliers}
            renderValue={(selected) =>
              selected.length === 0
                ? 'All suppliers'
                : selected.map((supplier) => GHA_SUPPLIER_LABELS[supplier] ?? supplier).join(', ')
            }
            onChange={(event) => {
              const next = event.target.value;
              onChange('suppliers', typeof next === 'string' ? next.split(',') : next);
            }}
          >
            {GHA_SUPPLIER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox size="small" checked={value.suppliers.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel id={`${idPrefix}-tiers`} shrink>
            CUG tiers
          </InputLabel>
          <Select
            labelId={`${idPrefix}-tiers`}
            label="CUG tiers"
            multiple
            displayEmpty
            notched
            value={value.cugTiers}
            renderValue={(selected) =>
              selected.length === 0
                ? 'All tiers'
                : GHA_SCOPE_CUG_TIER_OPTIONS.filter((option) => (selected as Array<string>).includes(option.value))
                    .map((option) => option.label)
                    .join(', ')
            }
            onChange={(event) => {
              const next = event.target.value;
              onChange('cugTiers', (typeof next === 'string' ? next.split(',') : next) as Array<GHAScopeRuleCugTier>);
            }}
          >
            {GHA_SCOPE_CUG_TIER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox size="small" checked={value.cugTiers.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Autocomplete
        multiple
        freeSolo
        fullWidth
        size="small"
        options={chainOptions ?? []}
        value={value.chainNames}
        onChange={(_, next) => onChange('chainNames', next)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Chains"
            helperText={
              <>
                Matched case-insensitively, whole value
                <br />
                Empty = any chain
              </>
            }
          />
        )}
      />
      <Autocomplete
        multiple
        freeSolo
        fullWidth
        size="small"
        options={[]}
        value={value.userCountries}
        onChange={(_, next) =>
          onChange(
            'userCountries',
            next.map((country) => country.toUpperCase()),
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Viewer countries"
            helperText={
              <>
                2-letter ISO, press Enter after each
                <br />
                {countriesEmptyHelper}
              </>
            }
          />
        )}
      />
    </>
  );
}
